import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';

import { useGetAccountOpportunities, useGetOffer } from '~/hooks/apis/offers/useGetOffer';
import { useGetOpportunityRelated } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { useEgsDraftsContext, usePackageOptionsContext } from '../PricingToolSessionContext';
import { chosenEgsRooms, egsRoomRows, reducePreviousEgs, withChosenEgsRooms } from '../tabs/egs/previousEgs';
import { deriveOpportunityCurrency } from '../tabs/inclusions/inclusionsModel';
import { previousLengthsOfStay, reducePreviousDealOptions } from '../tabs/packageOptions/previousDealOptions';

import CopyEgsRoomsStep from './CopyEgsRoomsStep';
import CopySectionOption from './CopySectionOption';
import {
  COPY_SECTIONS,
  COPY_STEP_LABELS,
  CopySection,
  CopySectionDetails,
  CopyStep,
  canCopy,
  copySteps,
  dealOptionsNotes,
  dealOptionsSummary,
  egsNotes,
  egsRoomNamesAfterCopy,
  egsSummary,
  isStepComplete,
  previousOpportunityChoices,
  replaceWarning,
} from './copyFromPreviousModel';

const AVAILABLE_SECTIONS = new Set<CopySection>(['dealOptions', 'extraGuestSurcharges']);

type CopyFromPreviousOpportunityDialogProps = {
  offerId: string;
  onClose: () => void;
  open: boolean;
};

export default function CopyFromPreviousOpportunityDialog({
  offerId,
  onClose,
  open,
}: CopyFromPreviousOpportunityDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { optionState, replaceFirstOptionWithPreviousRooms, roomAssets } = usePackageOptionsContext();
  const { replaceDrafts } = useEgsDraftsContext();
  const [step, setStep] = useState<CopyStep>('opportunity');
  const [sourceOpportunityId, setSourceOpportunityId] = useState('');
  const [selectedSections, setSelectedSections] = useState<Set<CopySection>>(new Set());
  const [pickedEgsRooms, setPickedEgsRooms] = useState<Record<string, Array<string>>>({});

  const { data: offer } = useGetOffer(offerId);
  const accountId = offer?.vendor_account_id;
  const {
    data: opportunities,
    error: opportunitiesError,
    isLoading: isLoadingOpportunities,
  } = useGetAccountOpportunities(accountId, { enabled: open });

  const choices = useMemo(() => previousOpportunityChoices(opportunities ?? [], offerId), [opportunities, offerId]);
  const source = choices.find((choice) => choice.id === sourceOpportunityId);
  const copiesDealOptions = selectedSections.has('dealOptions');
  const copiesEgs = selectedSections.has('extraGuestSurcharges');
  const readsRecords = copiesDealOptions || copiesEgs;

  const {
    data: related,
    error: relatedError,
    isFetching: isFetchingRelated,
  } = useGetOpportunityRelated(sourceOpportunityId, { enabled: open && !!sourceOpportunityId && readsRecords });
  const {
    data: targetRelated,
    error: targetRelatedError,
    isFetching: isFetchingTarget,
  } = useGetOpportunityRelated(offerId, {
    enabled: open && readsRecords,
  });

  const netCurrency = targetRelated ? deriveOpportunityCurrency(targetRelated.deals) : undefined;
  const previous = useMemo(
    () =>
      related && netCurrency
        ? reducePreviousDealOptions(related.deals, related.bundled_experiences, netCurrency)
        : undefined,
    [related, netCurrency],
  );
  const previousRoomCount = previous?.rooms.length ?? 0;
  const copiedDealOptions = copiesDealOptions && previousRoomCount > 0 ? previous : undefined;
  const previousEgs = useMemo(
    () =>
      related && netCurrency
        ? reducePreviousEgs(
            related.extra_guest_surcharges,
            netCurrency,
            egsRoomNamesAfterCopy(targetRelated, optionState, roomAssets, copiedDealOptions),
          )
        : undefined,
    [related, targetRelated, netCurrency, optionState, roomAssets, copiedDealOptions],
  );
  const previousEgsCount = previousEgs?.records.length ?? 0;
  const egsRows = useMemo(() => (previousEgs ? egsRoomRows(previousEgs) : []), [previousEgs]);
  const egsRoomsByNameKey = useMemo(
    () => chosenEgsRooms(egsRows, pickedEgsRooms, previousEgs?.roomNames ?? []),
    [egsRows, pickedEgsRooms, previousEgs],
  );
  const previousLos = useMemo(() => (previous ? previousLengthsOfStay(previous) : []), [previous]);
  const sectionDetails = useMemo(() => {
    const details: Partial<Record<CopySection, CopySectionDetails>> = {};
    if (previous && netCurrency) {
      details.dealOptions = previous.rooms.length
        ? { summary: dealOptionsSummary(previous, previousLos), notes: dealOptionsNotes(previous, netCurrency) }
        : { emptyMessage: 'That Opportunity has no Deal Options that can be copied.', notes: [] };
    }
    if (previousEgs && netCurrency) {
      details.extraGuestSurcharges = previousEgs.records.length
        ? { summary: egsSummary(previousEgs), notes: egsNotes(previousEgs, netCurrency) }
        : { emptyMessage: 'That Opportunity has no extra guest surcharges that can be copied.', notes: [] };
    }
    return details;
  }, [previous, previousLos, previousEgs, netCurrency]);

  const steps = copySteps(selectedSections);
  const stepIndex = steps.indexOf(step);
  const readiness = {
    hasSource: !!source,
    selected: selectedSections,
    isFetching: isFetchingRelated || isFetchingTarget,
    dealOptionRooms: previousRoomCount,
    egsRecords: previousEgsCount,
    everyEgsHasRooms: Object.values(egsRoomsByNameKey).every((rooms) => rooms.length > 0),
  };
  const warning = replaceWarning(selectedSections);

  const handleEgsRoomsChange = useCallback((key: string, rooms: Array<string>) => {
    setPickedEgsRooms((current) => ({ ...current, [key]: rooms }));
  }, []);

  function handleSourceChange(event: React.ChangeEvent<HTMLInputElement>) {
    setSourceOpportunityId(event.target.value);
    setPickedEgsRooms({});
  }

  function handleSectionChange(event: React.ChangeEvent<HTMLInputElement>) {
    const section = COPY_SECTIONS.find(({ key }) => key === event.target.name)?.key;
    const { checked } = event.target;
    if (!section) {
      return;
    }
    setSelectedSections((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(section);
      } else {
        next.delete(section);
      }
      return next;
    });
  }

  function handleCopy() {
    if (!source) {
      return;
    }
    if (copiedDealOptions) {
      replaceFirstOptionWithPreviousRooms(copiedDealOptions);
    }
    if (copiesEgs && previousEgs) {
      replaceDrafts(withChosenEgsRooms(previousEgs, egsRoomsByNameKey));
    }
    enqueueSnackbar(`Copied from ${source.name}`, { variant: 'success' });
    onClose();
  }

  function handleExited() {
    setStep('opportunity');
    setSourceOpportunityId('');
    setSelectedSections(new Set());
    setPickedEgsRooms({});
  }

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      onClose={onClose}
      open={open}
      slotProps={{ transition: { onExited: handleExited } }}
    >
      <DialogTitle>Copy from previous Opportunity</DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={stepIndex} sx={{ mb: 3 }}>
          {steps.map((copyStep) => (
            <Step key={copyStep}>
              <StepLabel>{COPY_STEP_LABELS[copyStep]}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 'opportunity' && (
          <>
            <Typography color="text.secondary" variant="body2">
              Pick a previous Opportunity for this property. Nothing is changed in Salesforce.
            </Typography>

            {isLoadingOpportunities && <CircularProgress size={24} sx={{ mt: 2 }} />}

            {opportunitiesError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Unable to load the previous Opportunities for this property.
              </Alert>
            )}

            {opportunities && choices.length === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                This property has no other Opportunities.
              </Alert>
            )}

            {choices.length > 0 && (
              <RadioGroup onChange={handleSourceChange} sx={{ mt: 2 }} value={sourceOpportunityId}>
                {choices.map((choice) => (
                  <FormControlLabel
                    control={<Radio size="small" />}
                    key={choice.id}
                    label={
                      <Stack>
                        <Typography variant="body2">{choice.name}</Typography>
                        <Typography color="text.secondary" variant="caption">
                          {[choice.stage, choice.closeDate && `Close date ${choice.closeDate}`]
                            .filter(Boolean)
                            .join(' · ')}
                        </Typography>
                      </Stack>
                    }
                    value={choice.id}
                  />
                ))}
              </RadioGroup>
            )}
          </>
        )}

        {step === 'sections' && (
          <>
            <Typography color="text.secondary" variant="body2">
              Pick what to copy from {source?.name}.
            </Typography>

            <FormGroup sx={{ mt: 2 }}>
              {COPY_SECTIONS.map((section) => (
                <CopySectionOption
                  checked={selectedSections.has(section.key)}
                  disabled={!AVAILABLE_SECTIONS.has(section.key)}
                  details={isFetchingRelated ? undefined : sectionDetails[section.key]}
                  key={section.key}
                  label={section.label}
                  name={section.key}
                  onChange={handleSectionChange}
                />
              ))}
            </FormGroup>

            {readsRecords && isFetchingRelated && <CircularProgress size={24} sx={{ mt: 2 }} />}

            {readsRecords && !isFetchingRelated && relatedError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Unable to load that Opportunity from Salesforce.
              </Alert>
            )}

            {readsRecords && !isFetchingTarget && targetRelatedError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Unable to load this Opportunity from Salesforce, so nothing can be copied.
              </Alert>
            )}
          </>
        )}

        {step === 'egsRooms' && previousEgs && (
          <CopyEgsRoomsStep
            onRoomsChange={handleEgsRoomsChange}
            roomNames={previousEgs.roomNames}
            rows={egsRows}
            roomsByNameKey={egsRoomsByNameKey}
          />
        )}

        {step === 'confirm' && (
          <>
            <Typography variant="body2">From {source?.name}:</Typography>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              {COPY_SECTIONS.filter((section) => selectedSections.has(section.key)).map((section) => (
                <Typography component="li" key={section.key} variant="body2">
                  {`${section.label}: ${sectionDetails[section.key]?.summary}`}
                </Typography>
              ))}
            </Box>

            {warning && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {warning}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text">
          Cancel
        </Button>
        {stepIndex > 0 && (
          <Button onClick={() => setStep(steps[stepIndex - 1])} variant="text">
            Back
          </Button>
        )}
        {step === 'confirm' ? (
          <Button disabled={!canCopy(readiness)} onClick={handleCopy} variant="contained">
            Copy
          </Button>
        ) : (
          <Button
            disabled={!isStepComplete(step, readiness)}
            onClick={() => setStep(steps[stepIndex + 1])}
            variant="contained"
          >
            Continue
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
